import { Role } from '@/services/authentication/authentication.types';
import {
  buildDivisionNode,
  buildPath,
  collectInitialExpandedIds,
  countDivisions,
  countLocations,
  expandToNode,
  flattenTree,
  getChildDivisions,
  getLocations,
  TreeNode
} from '@/services/hierachy/organisationHierarchy.utils';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { IconType } from 'react-icons';
import { GiPokecog } from 'react-icons/gi';
import {
  HiChevronDown,
  HiChevronRight,
  HiOutlineLibrary,
  HiOutlineLocationMarker,
  HiOutlineOfficeBuilding
} from 'react-icons/hi';
import { NavPage } from '../../../components/page/Breadcrumbs';
import { PageContainer } from '../../../components/page/PageContainer';
import { PageHeader } from '../../../components/page/PageHeader';
import { PageSizer, SizerMode } from '../../../components/page/PageSizer';
import { TSCard, TSTitleCard } from '../../../components/shared/card/Card';
import { Icon } from '../../../components/shared/icons/Icon';
import { Search } from '../../../components/shared/Search';
import { TimeshiftAvatar } from '../../../components/shared/TimeshiftAvatar';
import { isManagement } from '../../../services/authentication/authentication-service';
import { RoleType } from '../../../services/authentication/roles';
import { handleError } from '../../../services/gql/config/URQLProvider';
import {
  ReadEmployee,
  useGetEmployeesQuery,
  useGetOrganisationHierarchyQuery
} from '../../../services/gql/graphql.generated';
import { displayName } from '../../../services/translation/name';
import strings from '../manageOrg.translations';

const isCurrentEmployee = (employee: ReadEmployee) => {
  if (!employee.hireDate && !employee.leaveDate) {
    return true;
  }

  const today = new Date();

  if (employee.hireDate && new Date(employee.hireDate) > today) {
    return false;
  }

  if (employee.leaveDate && new Date(employee.leaveDate) < today) {
    return false;
  }

  return true;
};

const isManagerEmployee = (employee: ReadEmployee) => {
  const role = employee.role as Role | undefined;
  return Boolean(isManagement(role) || employee.personas?.some(persona => Boolean(persona?.management)));
};

const isSeniorManagerEmployee = (employee: ReadEmployee) => employee.role === RoleType.Senior_Manager;

const isEmployeeAccount = (employee: ReadEmployee) => Boolean(employee.employeeId);

const CountPill: FC<{ icon: IconType; label: string; value: number }> = ({ icon, label, value }) => {
  return (
    <div className="rounded-full border border-primary-std bg-primary-50 px-4 py-2">
      <div className="flex items-center gap-2">
        <Icon Icon={icon} theme="primary" small />
        <span className="text-sm text-secondary-std">{label}</span>
        <span className="text-sm font-semibold">{value}</span>
      </div>
    </div>
  );
};

const DetailsList: FC<{ title: string; emptyLabel: string; children: React.ReactNode }> = ({
  title,
  emptyLabel,
  children
}) => {
  const hasChildren = Array.isArray(children) ? children.length > 0 : !!children;

  return (
    <div className="rounded-lg border border-primary-std bg-primary-std p-4">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-secondary-std">{title}</h3>
      {hasChildren ? children : <p className="text-sm text-secondary-std">{emptyLabel}</p>}
    </div>
  );
};

const EmployeeChip: FC<{ employee: ReadEmployee }> = ({ employee }) => {
  const name = displayName({ firstName: employee.firstName, lastName: employee.lastName }) ?? employee.userName;

  return (
    <div className="mb-2 mr-2 inline-flex items-center gap-3 rounded-full border border-primary-std bg-white py-2 pl-2 pr-4 text-sm">
      <TimeshiftAvatar name={name} src={employee.avatarUrl} size={32} />
      <span>{name}</span>
    </div>
  );
};

const TreeRow: FC<{
  node: TreeNode;
  level: number;
  selectedId: string;
  expandedIds: Set<string>;
  onSelect: (nodeId: string) => void;
  onToggle: (nodeId: string) => void;
  registerNodeRef: (nodeId: string, element: HTMLButtonElement | null) => void;
}> = ({ node, level, selectedId, expandedIds, onSelect, onToggle, registerNodeRef }) => {
  const isDivision = node.type === 'division';
  const isSelected = node.id === selectedId;
  const isExpanded = isDivision ? expandedIds.has(node.id) : false;
  const hasChildren = isDivision ? node.children.length > 0 || node.locations.length > 0 : false;

  return (
    <div>
      <button
        type="button"
        ref={element => registerNodeRef(node.id, element)}
        className={[
          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors',
          isSelected ? 'bg-black bg-opacity-[0.04]' : 'hover:bg-black hover:bg-opacity-5'
        ].join(' ')}
        style={{ paddingLeft: `${12 + level * 20}px` }}
        onClick={() => onSelect(node.id)}
      >
        {isDivision ? (
          <span
            className="flex h-5 w-5 items-center justify-center rounded-sm hover:bg-black hover:bg-opacity-5"
            onClick={event => {
              event.stopPropagation();
              onToggle(node.id);
            }}
          >
            {hasChildren ? (
              isExpanded ? (
                <HiChevronDown className="h-4 w-4" />
              ) : (
                <HiChevronRight className="h-4 w-4" />
              )
            ) : (
              <span className="h-4 w-4" />
            )}
          </span>
        ) : (
          <span className="h-5 w-5" />
        )}
        <Icon Icon={isDivision ? HiOutlineOfficeBuilding : HiOutlineLocationMarker} theme="primary" small />
        <div className="min-w-0">
          <div className={isDivision ? 'truncate font-semibold' : 'truncate'}>{node.name}</div>
          <div className="text-xs text-secondary-std">
            {isDivision
              ? strings.organisationStructureSection.divisionLabel
              : strings.organisationStructureSection.locationLabel}
          </div>
        </div>
      </button>

      {isDivision && isExpanded ? (
        <div className="space-y-1">
          {node.children.map(child => (
            <TreeRow
              key={child.id}
              node={child}
              level={level + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggle={onToggle}
              registerNodeRef={registerNodeRef}
            />
          ))}
          {node.locations.map(location => (
            <TreeRow
              key={location.id}
              node={location}
              level={level + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggle={onToggle}
              registerNodeRef={registerNodeRef}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const OrganisationStructurePage: FC = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: strings.manageOrgSection.title,
      to: '/manage-organisation'
    },
    {
      name: strings.organisationStructureSection.title,
      to: '/manage-organisation/structure'
    }
  ];

  const [{ data, error }] = useGetOrganisationHierarchyQuery();
  const [{ data: employeesData, error: employeesError }] = useGetEmployeesQuery();

  if (error) {
    handleError(error);
  }

  if (employeesError) {
    handleError(employeesError);
  }

  const hierarchy = data?.organisationHierarchy;

  const rootNode = useMemo(() => (hierarchy ? buildDivisionNode(hierarchy) : undefined), [hierarchy]);
  const nodeMap = useMemo(
    () => (rootNode ? flattenTree(rootNode, new Map<string, TreeNode>()) : new Map<string, TreeNode>()),
    [rootNode]
  );

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const nodeRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

  useEffect(() => {
    if (!rootNode) {
      return;
    }

    setExpandedIds(collectInitialExpandedIds(rootNode));
    setSelectedId(rootNode.id);
  }, [rootNode]);

  useEffect(() => {
    if (!selectedId) {
      return;
    }

    const selectedElement = nodeRefs.current.get(selectedId);
    if (!selectedElement) {
      return;
    }

    requestAnimationFrame(() => {
      selectedElement.scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
    });
  }, [selectedId, expandedIds]);

  const selectedNode = selectedId ? nodeMap.get(selectedId) : undefined;
  const selectedPath = selectedNode ? buildPath(selectedNode.id, nodeMap) : [];
  const parentNode =
    selectedNode?.parentId && nodeMap.has(selectedNode.parentId) ? nodeMap.get(selectedNode.parentId) : undefined;
  const activeEmployees = useMemo(
    () =>
      (employeesData?.employees ?? [])
        .filter((employee): employee is ReadEmployee => !!employee)
        .filter(isCurrentEmployee),
    [employeesData]
  );
  const selectedLocationEmployees = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'location') {
      return [];
    }

    return activeEmployees
      .filter(isEmployeeAccount)
      .filter(employee => employee.homeLocationId === selectedNode.locationId);
  }, [activeEmployees, selectedNode]);
  const selectedLocationSeniorManagers = useMemo(
    () => selectedLocationEmployees.filter(isSeniorManagerEmployee),
    [selectedLocationEmployees]
  );
  const selectedLocationManagers = useMemo(
    () =>
      selectedLocationEmployees.filter(employee => isManagerEmployee(employee) && !isSeniorManagerEmployee(employee)),
    [selectedLocationEmployees]
  );
  const selectedLocationStaff = useMemo(
    () => selectedLocationEmployees.filter(employee => !isManagerEmployee(employee)),
    [selectedLocationEmployees]
  );
  const searchMatches = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();

    if (!trimmedQuery) {
      return [];
    }

    return Array.from(nodeMap.values())
      .filter(node => node.name.toLowerCase().includes(trimmedQuery))
      .slice(0, 12);
  }, [nodeMap, searchQuery]);

  const toggleNode = (nodeId: string) => {
    setExpandedIds(previous => {
      const next = new Set(previous);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const selectNode = (nodeId: string) => {
    setExpandedIds(previous => {
      const next = new Set(previous);
      expandToNode(nodeId, nodeMap).forEach(id => next.add(id));
      return next;
    });
    setSelectedId(nodeId);
  };

  const registerNodeRef = (nodeId: string, element: HTMLButtonElement | null) => {
    nodeRefs.current.set(nodeId, element);
  };

  return (
    <PageContainer
      customHeader={
        <PageHeader
          pageTitle={strings.organisationStructureSection.title}
          breadcrumbs={breadcrumbs}
          icon={<GiPokecog />}
        />
      }
    >
      <PageSizer className="flex flex-col gap-5" mode={SizerMode.PAGE}>
        {hierarchy && rootNode ? (
          <>
            <TSTitleCard
              title={strings.organisationStructureSection.overviewTitle}
              descriptor={strings.organisationStructureSection.overviewDescription}
              icon={HiOutlineLibrary}
              className="w-full"
            >
              <div className="flex flex-wrap gap-3">
                <CountPill
                  icon={HiOutlineOfficeBuilding}
                  label={strings.organisationStructureSection.directDivisions}
                  value={getChildDivisions(hierarchy).length}
                />
                <CountPill
                  icon={HiOutlineLibrary}
                  label={strings.organisationStructureSection.totalDivisions}
                  value={countDivisions(hierarchy)}
                />
                <CountPill
                  icon={HiOutlineLocationMarker}
                  label={strings.organisationStructureSection.totalLocations}
                  value={countLocations(hierarchy)}
                />
                <CountPill
                  icon={HiOutlineLocationMarker}
                  label={strings.organisationStructureSection.rootLocations}
                  value={getLocations(hierarchy).length}
                />
              </div>
            </TSTitleCard>

            <div className="grid gap-5 xl:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]">
              <TSCard className="p-4">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold">{strings.organisationStructureSection.treeTitle}</h2>
                  <p className="text-sm text-secondary-std">{strings.organisationStructureSection.treeDescription}</p>
                </div>
                <div className="mb-4 space-y-3 rounded-lg border border-primary-std bg-primary-50 p-3">
                  <div>
                    <p className="text-sm font-semibold">{strings.common.search}</p>
                    <p className="text-xs text-secondary-std">{strings.organisationStructureSection.treeDescription}</p>
                  </div>
                  <div className="rounded-md bg-primary-std ">
                    <Search
                      onChange={setSearchQuery}
                      className="w-full"
                      inputClassName="w-full bg-transparent focus:outline-none"
                    />
                  </div>
                  {searchMatches.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {searchMatches.map(node => (
                        <button
                          key={node.id}
                          type="button"
                          className={[
                            'inline-flex items-center rounded-full border px-3 py-1 text-sm',
                            node.type === 'location'
                              ? 'border-blue-300 bg-blue-100 text-blue-800'
                              : 'border-amber-300 bg-amber-100 text-amber-900'
                          ].join(' ')}
                          onClick={() => selectNode(node.id)}
                        >
                          {node.type === 'location' ? (
                            <HiOutlineLocationMarker className="mr-2 h-4 w-4" />
                          ) : (
                            <HiOutlineOfficeBuilding className="mr-2 h-4 w-4" />
                          )}
                          {node.name}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <TreeRow
                    node={rootNode}
                    level={0}
                    selectedId={selectedId}
                    expandedIds={expandedIds}
                    onSelect={selectNode}
                    onToggle={toggleNode}
                    registerNodeRef={registerNodeRef}
                  />
                </div>
              </TSCard>

              <TSCard className="p-5">
                <div className="mb-5">
                  <h2 className="text-lg font-semibold">{strings.organisationStructureSection.detailsTitle}</h2>
                  <p className="text-sm text-secondary-std">
                    {strings.organisationStructureSection.detailsDescription}
                  </p>
                </div>

                {selectedNode ? (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-primary-std bg-primary-50 p-4">
                      <div className="mb-2 flex items-center gap-2">
                        <Icon
                          Icon={selectedNode.type === 'division' ? HiOutlineOfficeBuilding : HiOutlineLocationMarker}
                          theme="primary"
                          small
                        />
                        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-secondary-std">
                          {strings.organisationStructureSection.currentSelection}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold">{selectedNode.name}</h3>
                      <p className="mt-2 text-sm text-secondary-std">
                        {selectedPath.map(node => node.name).join(' / ')}
                      </p>
                      {parentNode ? (
                        <p className="mt-2 text-sm text-secondary-std">
                          {strings.organisationStructureSection.parentLabel}: {parentNode.name}
                        </p>
                      ) : null}
                    </div>

                    {selectedNode.type === 'division' ? (
                      <>
                        <DetailsList
                          title={strings.organisationStructureSection.childDivisions}
                          emptyLabel={strings.organisationStructureSection.noChildDivisions}
                        >
                          {selectedNode.children.map(child => (
                            <button
                              key={child.id}
                              type="button"
                              className="mb-2 mr-2 inline-flex items-center rounded-full border border-primary-std bg-primary-50 px-3 py-2 text-sm"
                              onClick={() => {
                                selectNode(child.id);
                              }}
                            >
                              <HiOutlineOfficeBuilding className="mr-2 h-4 w-4" />
                              {child.name}
                            </button>
                          ))}
                        </DetailsList>

                        <DetailsList
                          title={strings.organisationStructureSection.locations}
                          emptyLabel={strings.organisationStructureSection.noLocations}
                        >
                          {selectedNode.locations.map(location => (
                            <button
                              key={location.id}
                              type="button"
                              className="mb-2 mr-2 inline-flex items-center rounded-full border border-primary-std bg-white px-3 py-2 text-sm"
                              onClick={() => selectNode(location.id)}
                            >
                              <HiOutlineLocationMarker className="mr-2 h-4 w-4" />
                              {location.name}
                            </button>
                          ))}
                        </DetailsList>
                      </>
                    ) : (
                      <>
                        <DetailsList
                          title={strings.organisationStructureSection.seniorManagers}
                          emptyLabel={strings.organisationStructureSection.noSeniorManagers}
                        >
                          {selectedLocationSeniorManagers.map(employee => (
                            <EmployeeChip key={employee.identityId ?? employee.userName} employee={employee} />
                          ))}
                        </DetailsList>

                        <DetailsList
                          title={strings.organisationStructureSection.managers}
                          emptyLabel={strings.organisationStructureSection.noManagers}
                        >
                          {selectedLocationManagers.map(employee => (
                            <EmployeeChip key={employee.identityId ?? employee.userName} employee={employee} />
                          ))}
                        </DetailsList>

                        <DetailsList
                          title={strings.organisationStructureSection.employees}
                          emptyLabel={strings.organisationStructureSection.noEmployees}
                        >
                          {selectedLocationStaff.map(employee => (
                            <EmployeeChip key={employee.identityId ?? employee.userName} employee={employee} />
                          ))}
                        </DetailsList>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-secondary-std">{strings.organisationStructureSection.browseHint}</p>
                )}
              </TSCard>
            </div>
          </>
        ) : (
          <TSCard className="flex justify-center px-6 py-6 font-semibold">
            {strings.organisationStructureSection.noData}
          </TSCard>
        )}
      </PageSizer>
    </PageContainer>
  );
};
